import React, { Component, PropTypes } from 'react';
import { connect } from 'react-redux';
import Helmet from 'react-helmet';
import Formsy from 'formsy-react';
import Dropzone from 'react-dropzone';
import { autobind } from 'core-decorators';
import classNames from 'classnames';

import { patchUser, uploadUserImage, deleteUserImage } from 'services/api';
import { updateUser, login } from 'actions';
import { byIdShape } from 'utils/propTypes';

import { Input, Radio } from 'AppComponents/Forms/Formsy';
import SubmitButton from 'AppComponents/SubmitButton';
import PageHeader from 'AppComponents/PageHeader';
import UserImage from 'AppComponents/UserImage';
import Alert from 'AppComponents/Alert';

import ChangePassword from './ChangePassword';

import './styles.scss';


const mapInputs = inputs => (
  Object.assign({}, inputs, {
    imperial_units: inputs.units === 'imperial',
  })
);

const mapValues = values => (
  Object.assign({}, values, {
    units: values.imperial_units ? 'imperial' : 'metric',
  })
);


class UserAccount extends Component {

  static propTypes = {
    currentUser: PropTypes.number.isRequired,
    loginRequest: PropTypes.func.isRequired,
    updateUser: PropTypes.func.isRequired,
    usersById: byIdShape.isRequired,
  };

  static contextTypes = {
    router: PropTypes.object.isRequired,
  };

  constructor(props, context) {
    super(props, context);
    this.state = {
      enableSubmit: false,
      isSubmitting: false,
      serverError: null,
      successMsg: null,
      imageState: null,
      imageError: null,
      imagePreview: null,
      showPasswordChange: false,
    };
  }

  shouldComponentUpdate(nextProps) {
    if (!nextProps.currentUser) {
      this.context.router.push('/user/login/');
      return false;
    }
    return true;
  }

  @autobind
  handleSubmit(data, resetForm, invalidateForm) {
    const user = this.props.usersById[this.props.currentUser];
    this.setState({
      isSubmitting: true,
      successMsg: null,
    }, () => {
      patchUser(user.username, data).then(({ response, error }) => {
        this.setState({ isSubmitting: false });
        if (error) {
          if (typeof error === 'object') {
            this.setState({ serverError: error.detail });
            delete error.detail;
            const errors = {};
            Object.keys(error).forEach((field) => {
              errors[field] = error[field][0];
            });
            invalidateForm(errors);
          } else {
            this.setState({ serverError: 'An unknown error occurred. Please try again.' });
          }
        } else {
          this.props.updateUser(response);
          resetForm(mapValues(response));
          this.setState({ successMsg: 'Your changes have been saved.' });
        }
      });
    });
  }

  @autobind
  handleImageDrop(files) {
    const image = files[0];
    if (image.type.substring(0, 5) === 'image') {
      const user = this.props.usersById[this.props.currentUser];
      this.setState({
        imageState: 'uploading',
        imagePreview: image.preview,
      });
      uploadUserImage(user.username, image).then(({ response, error }) => {
        if (error) {
          this.setState({ imageState: 'error' });
          if (error.detail) {
            this.setState({ imageError: error.detail });
          } else {
            this.setState({
              imageError: 'An unknown error occurred uploading your image. Please try again.',
            });
          }
        } else {
          this.props.updateUser(response);
          this.setState({
            imageError: null,
            successMsg: 'Your profile photo has been updated.',
            imageState: 'success',
          });
        }
      });
    } else {
      this.setState({
        imageState: 'error',
        imageError: 'File must be a valid image!',
      });
    }
  }

  @autobind
  handleImageDelete() {
    const user = this.props.usersById[this.props.currentUser];
    this.setState({ imageState: 'deleting' });
    deleteUserImage(user.username).then(({ response, error }) => {
      if (error) {
        this.setState({ imageState: 'error' });
        if (error.detail) {
          this.setState({ imageError: error.detail });
        } else {
          this.setState({
            imageError: 'An unknown error occurred uploading your image. Please try again.',
          });
        }
      } else {
        this.props.updateUser(response);
        this.setState({
          imageError: null,
          successMsg: null,
          imageState: 'success',
        });
      }
    });
  }

  @autobind
  handleShowPasswordChange() {
    this.toggleShowPasswordChange();
  }

  toggleShowPasswordChange() {
    this.setState({ showPasswordChange: !this.state.showPasswordChange });
  }

  @autobind
  handleChangePasswordClose() {
    this.setState({ showPasswordChange: false });
  }

  @autobind
  handleChangePasswordSuccess(data) {
    this.handleChangePasswordClose();
    this.props.loginRequest(data.username, data.password);
  }

  render() {
    const { imageState } = this.state;
    const user = this.props.usersById[this.props.currentUser];

    return (
      <div className='container' styleName='root'>

        <Helmet title='My Account' />

        <PageHeader title='My Account' />

        <Alert type='danger' display={this.state.serverError && true}>
          <strong>Error: </strong>{this.state.serverError}
        </Alert>

        <Alert type='success' display={this.state.successMsg && true} dismissible>
          {this.state.successMsg}
        </Alert>

        <div className='row'>

          <div
            className={classNames('user-image', {
              'has-error': imageState === 'error',
              uploading: imageState === 'uploading',
              'has-success': imageState === 'success',
              deleting: imageState === 'deleting',
            })}
            styleName='user-image'
          >

            <Dropzone
              className='dropzone'
              styleName='dropzone'
              activeClassName='active'
              onDrop={this.handleImageDrop}
              multiple={false}
              accept='image/*'
            >
              { this.state.imageState === 'uploading' ?
                <img
                  alt='Preview'
                  className='preview'
                  styleName='preview'
                  src={this.state.imagePreview}
                />
              :
                <UserImage user={user} />
              }
            </Dropzone>

            { user.image ?
              <button
                className='delete'
                styleName='delete'
                onClick={this.handleImageDelete}
              >
                  &times;
              </button>
            : null }

            <p className='help-block'>
              Upload an image by dragging into or clicking the box above.
            </p>

            { this.state.imageError ?
              <p className='help-block'>
                <strong>Error: </strong>{ this.state.imageError }
              </p>
            : null }

          </div>

          <Formsy.Form
            className='form-horizontal with-feedback user-form'
            styleName='user-form'
            onValid={() => this.setState({ enableSubmit: true })}
            onInvalid={() => this.setState({ enableSubmit: false })}
            onValidSubmit={this.handleSubmit}
            mapping={mapInputs}
            noValidate
          >

            <Input
              name='first_name'
              label='First Name'
              value={user.first_name}
            />

            <Input
              name='last_name'
              label='Last Name'
              value={user.last_name}
            />

            <Input
              type='email'
              name='email'
              label='Email Address'
              value={user.email}
              validations={{
                isEmail: true,
              }}
              validationErrors={{
                isEmail: 'Must be a valid email.',
              }}
              required
            />

            <Radio
              name='units'
              label='Units'
              options={[
                { value: 'metric', label: 'Metric', help: 'meters, kilometers' },
                { value: 'imperial', label: 'Imperial', help: 'feet, miles' },
              ]}
              value={user.imperial_units ? 'imperial' : 'metric'}
              required
            />

            <div className='form-footer'>
              <div className='aligned-with-input'>

                <SubmitButton
                  label='Save'
                  enabled={this.state.enableSubmit}
                  isSubmitting={this.state.isSubmitting}
                />

                <button
                  type='button'
                  className='btn btn-secondary'
                  onClick={this.handleShowPasswordChange}
                >
                  Change Password
                </button>

              </div>
            </div>

          </Formsy.Form>

        </div>

        <ChangePassword
          display={this.state.showPasswordChange}
          handleClose={this.handleChangePasswordClose}
          handleSuccess={this.handleChangePasswordSuccess}
        />

      </div>
    );
  }
}

const mapStateToProps = state => ({
  usersById: state.users.byId,
  currentUser: state.auth.currentUser,
});

const loginRequest = login.request;

export default connect(
  mapStateToProps, {
    updateUser,
    loginRequest,
  },
)(UserAccount);



// WEBPACK FOOTER //
// ./app/scripts/views/App/views/UserAccount/index.jsx